import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import Landing from './pages/Landing';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Create from './pages/Create';
import Scorecard from './pages/Scorecard';
import Carousel from './pages/Carousel';
import Profile from './pages/Profile';
import AdminAgents from './pages/AdminAgents';
import AdminSettings from './pages/AdminSettings';
import AdminBillingSettings from './pages/AdminBillingSettings';
import AdminUsers from './pages/AdminUsers';
import AdminUserDetail from './pages/AdminUserDetail';
import AdminUserConversations from './pages/AdminUserConversations';
import AdminFormConfig from './pages/AdminFormConfig';
import AdminStats from './pages/AdminStats';
import AdminCreditPlans from './pages/AdminCreditPlans';
import AdminReviewAgents from './pages/AdminReviewAgents';
import AdminReviewLogs from './pages/AdminReviewLogs';
import AgentReviewConfig from './pages/AgentReviewConfig';
import PendingApproval from './pages/PendingApproval';
import CaseLibrary from './pages/CaseLibrary';
import CaseDetail from './pages/CaseDetail';
import AdminCaseLibrary from './pages/AdminCaseLibrary';
import AdminCaseCategories from './pages/AdminCaseCategories';
import AdminDashboard from './pages/AdminDashboard';
import AdminUserVideos from './pages/AdminUserVideos';
import AdminScorecardConfig from './pages/AdminScorecardConfig';
import AdminCarouselPrompts from './pages/AdminCarouselPrompts';
import AdminDiagnostics from './pages/AdminDiagnostics';
import AdminFeedback from './pages/AdminFeedback';
import AdminOperatorLogs from './pages/AdminOperatorLogs';
import AdminPaymentLogs from './pages/AdminPaymentLogs';
import AdminSubAgents from './pages/AdminSubAgents';
import AdminLayout from './components/AdminLayout';
import './App.css';

const ProtectedRoute = ({ children, allowPending = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div data-theme="dark" className="min-h-screen flex items-center justify-center surface-obsidian">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-obsidian-600 border-t-lime-500 mx-auto mb-3"></div>
          <p className="text-obsidian-300 label-micro">LOADING</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Check if user is pending and not on pending approval page
  if (!allowPending && user.status === 'pending') {
    return <Navigate to="/pending-approval" replace />;
  }

  return children;
};

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div data-theme="dark" className="min-h-screen flex items-center justify-center surface-obsidian">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-obsidian-600 border-t-lime-500 mx-auto mb-3"></div>
          <p className="text-obsidian-300 label-micro">LOADING</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={user ? (
        user.role === 'operator' ? <Navigate to="/admin/cases" replace /> : <Navigate to="/create" replace />
      ) : <Landing />} />
      <Route path="/login" element={user ? (
        user.role === 'operator' ? <Navigate to="/admin/cases" replace /> : <Navigate to="/create" replace />
      ) : <Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/pending-approval"
        element={
          <ProtectedRoute allowPending={true}>
            <PendingApproval />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create"
        element={
          <ProtectedRoute>
            <Create />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scorecard"
        element={
          <ProtectedRoute>
            <Scorecard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/carousel"
        element={
          <ProtectedRoute>
            <Carousel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases"
        element={
          <ProtectedRoute>
            <CaseLibrary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases/:caseId"
        element={
          <ProtectedRoute>
            <CaseDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/agents"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminAgents /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/agents/:agentId/subagents"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminSubAgents /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminSettings /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/billing"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminBillingSettings /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminUsers /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:userId"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminUserDetail /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:userId/conversations"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminUserConversations /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/stats"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminStats /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/credit-plans"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminCreditPlans /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/form-config"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminFormConfig /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/review-agents"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminReviewAgents /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/review-logs"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminReviewLogs /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/agents/:agentId/review-config"
        element={
          <ProtectedRoute>
            <AdminLayout><AgentReviewConfig /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cases"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminCaseLibrary /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/case-categories"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminCaseCategories /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/form-config"
        element={
          <ProtectedRoute>
            <AdminFormConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/stats"
        element={
          <ProtectedRoute>
            <AdminStats />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/credit-plans"
        element={
          <ProtectedRoute>
            <AdminCreditPlans />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/review-agents"
        element={
          <ProtectedRoute>
            <AdminReviewAgents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/agents/:agentId/review-config"
        element={
          <ProtectedRoute>
            <AgentReviewConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/review-logs"
        element={
          <ProtectedRoute>
            <AdminReviewLogs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/user-videos"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminUserVideos /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/scorecard-config"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminScorecardConfig /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/carousel-prompts"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminCarouselPrompts /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/diagnostics"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminDiagnostics /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/feedback"
        element={
          <ProtectedRoute>
            <AdminLayout><AdminFeedback /></AdminLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Operator Logs */}
      <Route
        path="/admin/operator-logs"
        element={
          <ProtectedRoute>
            <AdminOperatorLogs />
          </ProtectedRoute>
        }
      />

      {/* Payment Logs */}
      <Route
        path="/admin/payment-logs"
        element={
          <ProtectedRoute>
            <AdminPaymentLogs />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="noise-overlay">
            <AppContent />
          </div>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;